/**
 * ProjectTasksList Component
 *
 * Renders project tasks with responsive layout:
 * - Desktop: Full-featured table with columns
 * - Mobile: Card-based list for better touch interaction
 *
 * Features:
 * - Search filtering
 * - Row/card selection for bulk actions
 * - Pagination
 * - Remove task action
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Badge, Tooltip, Button, Tag, Input, Empty, Pagination } from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  CloseOutlined,
  CalendarOutlined,
  CheckOutlined,
  RightOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { Link, useHistory } from 'react-router-dom';
import { startCase } from 'lodash';
import moment from 'moment';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import TaskMobileCards from '../shared/TaskMobileCards';
import { urgencyColors } from '../../configs/task.config';
import { projectStatusColors } from '../../configs/project.config';
import { text, space } from '../../configs/designTokens';
import '../../styles/index.scss';

const ProjectTasksList = ({
  tasks,
  milestones,
  selectedTaskIds,
  onSelectionChange,
  onRemoveTask,
  isMobile,
  isTablet,
}) => {
  const history = useHistory();
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter tasks by search
  const filteredTasks = useMemo(() => {
    if (!searchText) return tasks;
    return tasks.filter((task) =>
      task.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [tasks, searchText]);

  // Get milestone for a task
  const getTaskMilestone = (taskId) => {
    return (milestones || []).find((m) =>
      m.tasks?.some((t) => t.id === taskId)
    );
  };

  // Paginated tasks for mobile cards (client-side pagination)
  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredTasks.slice(startIndex, startIndex + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  const handleMobileTaskClick = (taskId) => {
    history.push(`/tasks/details/${taskId}`);
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <>
      <TaskMobileCards
        tasks={paginatedTasks}
        onTaskClick={handleMobileTaskClick}
        selectedIds={selectedTaskIds}
        onSelectionChange={onSelectionChange}
        onRemoveTask={onRemoveTask}
        milestones={milestones}
      />

      {/* Mobile Pagination */}
      {filteredTasks.length > 0 && (
        <div className="task-mobile-pagination">
          <div className="task-mobile-pagination__progress">
            <div className="task-mobile-pagination__progress-bar">
              <div
                className="task-mobile-pagination__progress-fill"
                style={{ width: `${Math.min(100, (Math.min(currentPage * pageSize, filteredTasks.length) / filteredTasks.length) * 100)}%` }}
              />
            </div>
            <span className="task-mobile-pagination__progress-text">
              {Math.min(currentPage * pageSize, filteredTasks.length)} of {filteredTasks.length}
            </span>
          </div>
          {filteredTasks.length > pageSize && (
            <div style={{ display: 'flex', gap: space[2] }}>
              <Button
                type="text"
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                type="text"
                disabled={currentPage >= Math.ceil(filteredTasks.length / pageSize)}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
                <RightOutlined />
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  // Desktop Table View
  const renderTable = () => {
    const columns = [
      {
        title: <span className="project-tasks__col-header">Task Name</span>,
        dataIndex: 'name',
        key: 'name',
        width: '35%',
        render: (text, record) => (
          <div className="project-tasks__name-cell">
            <Tooltip title={record.urgency ? `Urgency: ${startCase(record.urgency.toLowerCase())}` : undefined}>
              <Badge
                dot
                color={urgencyColors[record.urgency] || text.muted}
                className="project-tasks__urgency-badge"
              />
            </Tooltip>
            <div className="project-tasks__name-content">
              <Link to={`/tasks/details/${record.id}`} className="project-tasks__name-link">
                {text}
              </Link>
              {record.requestType && (
                <span className="project-tasks__request-type">
                  {record.requestType.split('_').map((word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  ).join(' ')}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        title: <span className="project-tasks__col-header">Due Date</span>,
        dataIndex: 'dueDate',
        key: 'dueDate',
        width: '12%',
        render: (dueDate) => {
          if (!dueDate) return <span className="project-tasks__empty-value">-</span>;

          const due = moment(dueDate);
          const daysLeft = due.diff(moment(), 'days');
          let className = 'project-tasks__due-date';
          if (daysLeft < 0) className += ' project-tasks__due-date--overdue';
          else if (daysLeft <= 3) className += ' project-tasks__due-date--soon';

          return <span className={className}>{due.format('MMM D, YYYY')}</span>;
        },
      },
      {
        title: <span className="project-tasks__col-header">Assignee</span>,
        dataIndex: 'assigneeUsers',
        key: 'assigneeUsers',
        width: '15%',
        render: (assigneeUsers) => {
          if (!assigneeUsers || assigneeUsers.length === 0) {
            return <span className="project-tasks__empty-value">-</span>;
          }

          return (
            <div className="project-tasks__assignees">
              {assigneeUsers.slice(0, 2).map((user) => (
                <Tooltip key={user.id} title={startCase(user.name || user.username)}>
                  <div className="project-tasks__assignee-avatar">
                    <AvatarS3
                      imgKey={user.avatar?.key}
                      username={user.name || user.username}
                      avatarProps={{ size: 26 }}
                    />
                  </div>
                </Tooltip>
              ))}
              {assigneeUsers.length > 2 && (
                <span className="project-tasks__assignee-more">+{assigneeUsers.length - 2}</span>
              )}
            </div>
          );
        },
      },
      {
        title: <span className="project-tasks__col-header">Milestone</span>,
        key: 'milestone',
        width: '15%',
        render: (_, record) => {
          const milestone = getTaskMilestone(record.id);
          return milestone ? (
            <Tag color="blue" className="project-tasks__milestone-tag">{milestone.name}</Tag>
          ) : (
            <span className="project-tasks__empty-value">-</span>
          );
        },
      },
      {
        title: <span className="project-tasks__col-header">Status</span>,
        dataIndex: 'status',
        key: 'status',
        width: '12%',
        render: (status) => {
          if (!status) return <span className="project-tasks__empty-value">N/A</span>;
          const formatted = status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
          return <Tag color={projectStatusColors[status] || 'default'}>{formatted}</Tag>;
        },
      },
      {
        title: '',
        key: 'actions',
        width: '60px',
        align: 'center',
        render: (_, record) => (
          <Tooltip title="Remove from project">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveTask(record.id, record.name);
              }}
              className="project-tasks__remove-btn"
            />
          </Tooltip>
        ),
      },
    ];

    const rowSelection = {
      selectedRowKeys: selectedTaskIds,
      preserveSelectedRowKeys: true,
      onChange: onSelectionChange,
      selections: [
        {
          key: 'select-all',
          text: 'Select All',
          onSelect: () => onSelectionChange(filteredTasks.map((t) => t.id)),
        },
        {
          key: 'clear-all',
          text: 'Clear All',
          onSelect: () => onSelectionChange([]),
        },
        Table.SELECTION_INVERT,
      ],
    };

    const total = filteredTasks.length;
    const paginationStart = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const paginationEnd = Math.min(currentPage * pageSize, total);

    const itemRender = (_page, type, originalElement) => {
      if (type === 'prev') {
        return (
          <Button type="text" icon={<LeftOutlined />}>
            Previous
          </Button>
        );
      }
      if (type === 'next') {
        return (
          <Button type="text">
            Next
            <RightOutlined />
          </Button>
        );
      }
      return originalElement;
    };

    return (
      <>
        <Table
          columns={columns}
          dataSource={filteredTasks}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            className: 'project-tasks__table-pagination--hidden',
          }}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No tasks found' }}
          className="project-tasks__table"
        />

        {total > 0 && (
          <div className="task-pagination-enhanced">
            <div className="task-pagination-enhanced__left">
              <span className="task-pagination-enhanced__summary">
                Showing <strong>{paginationStart}</strong>
                {' - '}
                <strong>{paginationEnd}</strong>
                {' of '}
                <strong>{total}</strong> tasks
              </span>
            </div>

            <div className="task-pagination-enhanced__center">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                hideOnSinglePage={false}
                onChange={(page) => setCurrentPage(page)}
                itemRender={itemRender}
                className="task-pagination-enhanced__nav"
                showLessItems={isTablet}
              />
            </div>

            <div className="task-pagination-enhanced__right">
              <span className="task-pagination-enhanced__size-label">Show:</span>
              <div className="task-pagination-enhanced__size-options">
                {[10, 25, 50, 100].map((size) => (
                  <button
                    key={size}
                    className={`task-pagination-enhanced__size-btn ${pageSize === size ? 'task-pagination-enhanced__size-btn--active' : ''}`}
                    onClick={() => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="project-tasks">
      {/* Search Bar */}
      <div className="project-tasks__search">
        <Input
          placeholder="Search tasks..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          prefix={<SearchOutlined className="project-tasks__search-icon" />}
          suffix={
            searchText && (
              <CloseOutlined
                className="project-tasks__search-clear"
                onClick={() => setSearchText('')}
              />
            )
          }
          className="project-tasks__search-input"
        />
        {searchText && (
          <span className="project-tasks__search-count">
            {filteredTasks.length} result{filteredTasks.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* List/Table */}
      {isMobile ? renderMobileCards() : renderTable()}
    </div>
  );
};

ProjectTasksList.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      urgency: PropTypes.string,
      dueDate: PropTypes.string,
      requestType: PropTypes.string,
      assigneeUsers: PropTypes.array,
    })
  ),
  milestones: PropTypes.array,
  selectedTaskIds: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
};

ProjectTasksList.defaultProps = {
  tasks: [],
  milestones: [],
  selectedTaskIds: [],
  isMobile: false,
  isTablet: false,
};

export default ProjectTasksList;
